import { useEffect, useRef, useState } from 'react';
import { UUID_REGEX } from '../model';
import { ArtifactsInfoResponse } from '../types';

type DeferredPromise<T> = {
  resolve: (value: T) => void;
  reject: (reason?: unknown) => void;
};

type CreateArtifactsInfoReceiverParams<TArgs extends unknown[]> = {
  getArtifactsInfo: (ids: string) => Promise<ArtifactsInfoResponse>;
  getBatchIds: (...args: TArgs) => string[];
};

const WAITING_TIMEOUT = 100;

export const filterValidArtifactIds = (ids: string[]) =>
  ids.filter(id => id && id !== 'undefined' && UUID_REGEX.test(id));

const useCreateArtifactsInfoReceiver = <TArgs extends unknown[]>({
  getArtifactsInfo,
  getBatchIds,
}: CreateArtifactsInfoReceiverParams<TArgs>) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [promises, setPromises] = useState<Array<DeferredPromise<ArtifactsInfoResponse>>>([]);
  const [batchIds, setBatchIds] = useState<string[]>([]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (promises.length && batchIds.length) {
      timeoutRef.current = setTimeout(async () => {
        const pendingPromises = promises;

        try {
          const uniqueBatchIds = [...new Set(batchIds)];
          const info = await getArtifactsInfo(uniqueBatchIds.join(','));

          pendingPromises.forEach(promise => promise.resolve(info));
        } catch (error) {
          pendingPromises.forEach(promise => promise.reject(error));
        } finally {
          timeoutRef.current = null;
          setBatchIds([]);
          setPromises([]);
        }
      }, WAITING_TIMEOUT);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [batchIds, getArtifactsInfo, promises]);

  return async (...args: TArgs) => {
    const normalizedBatchIds = getBatchIds(...args);

    if (!normalizedBatchIds.length) {
      return {};
    }

    const promise = new Promise<ArtifactsInfoResponse>((resolve, reject) =>
      setPromises(old => [...old, { resolve, reject }]),
    );

    setBatchIds(old => [...old, ...normalizedBatchIds]);

    const missedArtifactsInfo = await promise;

    // Invoice artifacts reponce without artifacts property is considered, so we return main object in this case
    return missedArtifactsInfo?.artifacts ?? missedArtifactsInfo;
  };
};

export default useCreateArtifactsInfoReceiver;
